import { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';

import { ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ReactionType } from '@/shared/types/tryIt';
import { isKeyEnter } from '@/util/util';

type MessageNodeProps = {
  active: boolean;
  handleNodeClick: () => void;
  index: number;
  resetLayout: () => void;
  reaction: ReactionType;
  request_id: string;
  headerSlot: ReactNode;
};

const MessageNode = ({
  active = false,
  handleNodeClick,
  children,
  index,
  resetLayout,
  reaction,
  request_id,
  headerSlot,
}: PropsWithChildren<MessageNodeProps>) => {
  const myRef = useRef<HTMLDivElement>(null);

  // This useEffect hook is used to observe changes in the size of the MessageNode component.
  // When the size (height in our case) of the element changes, the resetLayout function is called.
  useEffect(() => {
    const currentRef = myRef.current;
    const observer = new ResizeObserver(() => {
      resetLayout();
    });

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [resetLayout]);

  const isThumbsUp = reaction === '1';
  const isThumbsDown = reaction === '-1';

  return (
    <div
      id={request_id}
      ref={myRef}
      data-turn-row={index}
      className={cn(
        'group flex flex-col overflow-hidden transition',
        // Every row reserves the border, so selecting one does not shift its content.
        'cursor-default border-l-[3px] border-l-transparent',
        active ? 'border-l-selected bg-selected-soft' : 'hover:bg-selected-soft'
      )}
      onClick={handleNodeClick}
      onKeyDown={(e) => {
        if (isKeyEnter(e)) {
          handleNodeClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {headerSlot}
      <div className="flex min-w-0 flex-col gap-4 px-4 pt-2 pb-8 [&_.response]:mr-0 [&_.response]:self-stretch">
        {children}
      </div>

      {isThumbsUp && <ThumbsUpIcon className="mx-auto my-4 block text-success" />}
      {isThumbsDown && <ThumbsDownIcon className="mx-auto my-4 block text-destructive" />}
    </div>
  );
};

export default MessageNode;
